import { getAWSIntegrationSpecificValues } from 'components/status-checks/authentication-form-utils'
import { isExternalIntegrationOfType } from 'components/status-checks/authentication-form-validation-utils'
import { DEFAULT_POLL_INTERVAL } from 'components/status-checks/common/PollingIntervalField'
import {
  type EndpointConfiguration,
  ObservabilityToolType,
  PredicateType,
  type ResponseBodyEvaluation,
  type ServiceStatusCheckFromStatusCheck,
  type StatusCheck,
  type StatusCheckEvaluationPredicate,
  type StatusCheckHeader,
  type TeamExternalIntegration,
  type TeamExternalIntegrationResponse
} from 'types/api/status-checks/status-check'
import {
  convertToK6Url,
  k6LoadGeneratorIntegration
} from 'utils/load-generators/load-generator-integrations-util'
import { createLookupMapFromArray } from 'utils/obj'
import {
  appdynamicsResponseBodyEvaluationConfiguration,
  appdynamicsStatusCheckIntegration,
  convertToAppDynamicsBaseUrl,
  convertToAppDynamicsUrl
} from 'utils/status-checks/status-check-integrations-util-appdynamics'
import {
  awsResponseBodyEvaluationConfiguration,
  awsStatusCheckIntegration,
  convertToAWSApiUrl
} from 'utils/status-checks/status-check-integrations-util-aws'
import {
  convertToDatadogApiUrl,
  datadogResponseBodyEvaluationConfiguration,
  dataDogStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog'
import {
  convertToDatadogEUApiUrl,
  datadogEUResponseBodyEvaluationConfiguration,
  dataDogEUStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-eu'
import {
  convertToDatadogUS1FedApiUrl,
  datadogUS1FedResponseBodyEvaluationConfiguration,
  dataDogUS1FedStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-us1-fed'
import {
  convertToDatadogUS3ApiUrl,
  datadogUS3ResponseBodyEvaluationConfiguration,
  dataDogUS3StatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-us3'
import {
  convertToDatadogUS5ApiUrl,
  datadogUS5ResponseBodyEvaluationConfiguration,
  dataDogUS5StatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-datadog-us5'
import {
  convertToDynatraceBaseUrl,
  convertToDynatraceUrl,
  dynatraceResponseBodyEvaluationConfiguration,
  dynatraceStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-dynatrace'
import {
  convertToGrafanaBaseUrl,
  convertToGrafanaUrl,
  formatResponseBodyEvaluationPredicatesGrafana,
  grafanaResponseBodyEvaluationConfiguration,
  grafanaStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-grafana'
import {
  computeNewRelicResponseBodyEvaluationConfiguration,
  convertToNewRelicUrl,
  newRelicStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-newrelic'
import {
  convertToPagerDutyUrl,
  pagerDutyResponseBodyEvaluationConfiguration,
  pagerDutyStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-pagerduty'
import {
  prometheusResponseBodyEvaluationConfiguration,
  prometheusStatusCheckIntegration
} from 'utils/status-checks/status-check-integrations-util-prometheus'
import {
  ExternalIntegrationType,
  IntegrationType
} from 'utils/team-external-integrations/team-external-integration-types'
import { ENDPOINT_URLS } from 'utils/third-party'

import type { StatusCheckFormEvaluationConfiguration } from 'components/status-checks/status-checks-form-utils'

export type OnSuccessExternalIntegration = (
  response: TeamExternalIntegrationResponse
) => void

export const customStatusCheckIntegration: TeamExternalIntegration = {
  name: 'Custom',
  type: IntegrationType.Custom,
  authUrl: '',
  isPrivateNetworkIntegration: false,
  headers: [],
  url: '',
  sortIndex: 8
}

export const customLoadGeneratorIntegration: TeamExternalIntegration = {
  name: 'Other',
  type: IntegrationType.LoadGeneratorCustom,
  authUrl: '',
  isPrivateNetworkIntegration: false,
  headers: [],
  url: '',
  sortIndex: 9,
  isConfigured: false,
  isOther: true
}

export const teamExternalIntegrationsObject = {
  pagerduty: pagerDutyStatusCheckIntegration,
  newrelic: newRelicStatusCheckIntegration,
  datadog: dataDogStatusCheckIntegration,
  datadog_us3: dataDogUS3StatusCheckIntegration,
  datadog_us5: dataDogUS5StatusCheckIntegration,
  datadog_eu: dataDogEUStatusCheckIntegration,
  datadog_us1_fed: dataDogUS1FedStatusCheckIntegration,
  grafana: grafanaStatusCheckIntegration,
  dynatrace: dynatraceStatusCheckIntegration,
  appdynamics: appdynamicsStatusCheckIntegration,
  k6: k6LoadGeneratorIntegration,
  aws: awsStatusCheckIntegration
}

const SUPPORTED_EXTERNAL_INTEGRATIONS = [
  dataDogStatusCheckIntegration,
  dataDogUS3StatusCheckIntegration,
  dataDogUS5StatusCheckIntegration,
  dataDogEUStatusCheckIntegration,
  dataDogUS1FedStatusCheckIntegration,
  pagerDutyStatusCheckIntegration,
  newRelicStatusCheckIntegration,
  prometheusStatusCheckIntegration,
  grafanaStatusCheckIntegration,
  dynatraceStatusCheckIntegration,
  appdynamicsStatusCheckIntegration
] as const

export const defaultStatusCheckEvaluationConfiguration: StatusCheckFormEvaluationConfiguration =
  {
    healthyStatusCode: '200',
    isCustomRequestTimeout: false,
    requestTimeout: null,
    responseBodyEvaluation: {
      predicates: [],
      op: 'AND'
    }
  }

export const defaultPredicatesValue: StatusCheckEvaluationPredicate[] = [
  {
    type: PredicateType.String,
    jpQuery: '',
    jqQuery: '',
    comparator: '',
    rValue: ''
  }
]

export function getDomainNameFromIntegrationType(
  integrationType?: IntegrationType
) {
  switch (integrationType?.toLowerCase()) {
    case IntegrationType.Datadog:
      return 'US1'
    case IntegrationType.DatadogUS3:
      return 'US3'
    case IntegrationType.DatadogUS5:
      return 'US5'
    case IntegrationType.DatadogEU:
      return 'EU'
    case IntegrationType.DatadogUS1Fed:
      return 'US1 FED'
    default:
      return ''
  }
}

export function getDomainNameFromIntegration(
  integration: TeamExternalIntegration
) {
  const { domain, type: integrationType } = integration

  switch (integrationType?.toLowerCase()) {
    case IntegrationType.Datadog:
      return 'US1'
    case IntegrationType.DatadogUS3:
      return 'US3'
    case IntegrationType.DatadogUS5:
      return 'US5'
    case IntegrationType.DatadogEU:
      return 'EU'
    case IntegrationType.DatadogUS1Fed:
      return 'US1 FED'
    default:
      return domain
  }
}

export function formatExistingStatusCheck(
  statusCheck: StatusCheck
): ServiceStatusCheckFromStatusCheck {
  const {
    category,
    description,
    endpointConfiguration,
    evaluationConfiguration,
    identifier,
    isContinuous,
    isPrivateNetwork,
    multiSelectTags,
    name,
    pollingIntervalSeconds,
    rawEndpointConfiguration,
    requestParams,
    teamExternalIntegration
  } = statusCheck

  const integrationType: IntegrationType | undefined =
    teamExternalIntegration?.observabilityToolType
      ? getIntegrationTypeFromObservabilityToolTypeAndDomain(
          teamExternalIntegration?.observabilityToolType,
          teamExternalIntegration?.domain
        )
      : teamExternalIntegration?.type?.toLowerCase()

  return {
    category,
    description,
    evaluationConfiguration: {
      isCustomRequestTimeout: Boolean(evaluationConfiguration?.okLatencyMaxMs),
      healthyStatusCode: evaluationConfiguration?.okStatusCodes?.[0] ?? '',
      requestTimeout: evaluationConfiguration?.okLatencyMaxMs ?? null,
      ...(evaluationConfiguration.responseBodyEvaluation
        ? {
            responseBodyEvaluation: {
              op: evaluationConfiguration.responseBodyEvaluation.op,
              predicates:
                evaluationConfiguration.responseBodyEvaluation.predicates ?? []
            }
          }
        : { responseBodyEvaluation: { op: 'AND', predicates: [] } })
    },
    id: identifier === 'duplicate' ? '' : identifier,
    integration: {
      type: integrationType ?? IntegrationType.Custom,
      observabilityToolType: teamExternalIntegration?.observabilityToolType,
      domain:
        teamExternalIntegration?.domain ??
        getDomainNameFromIntegrationType(teamExternalIntegration?.type),
      name: teamExternalIntegration?.name ?? 'Custom',
      isPrivateNetworkIntegration: isPrivateNetwork,
      isConfigured: Boolean(teamExternalIntegration?.name),
      url:
        // Raw URL is the source of truth for the user form values
        rawEndpointConfiguration?.url ?? endpointConfiguration?.url,
      headers: Object.entries(endpointConfiguration?.headers ?? {}).map(
        ([key, value]) => ({ key, value, label: 'Value' })
      )
    },
    isContinuous,
    name,
    pollingIntervalSeconds: String(
      pollingIntervalSeconds ?? DEFAULT_POLL_INTERVAL
    ),
    ...(endpointConfiguration && { endpointConfiguration }),
    ...(teamExternalIntegration?.observabilityToolType ===
      ObservabilityToolType.AWS &&
      getAWSIntegrationSpecificValues(teamExternalIntegration)),
    ...(multiSelectTags && { multiSelectTags }),
    ...(requestParams && { requestParams })
  }
}

export function getIntegrationTypeFromObservabilityToolTypeAndDomain(
  observabilityToolType: ObservabilityToolType,
  domain: TeamExternalIntegration['domain']
) {
  if (observabilityToolType === ObservabilityToolType.DATADOG) {
    switch (domain) {
      case 'US3':
        return IntegrationType.DatadogUS3
      case 'US5':
        return IntegrationType.DatadogUS5
      case 'EU':
        return IntegrationType.DatadogEU
      // We have two different ways of expressing FED1
      case 'US1_FED':
      case 'US1 FED':
        return IntegrationType.DatadogUS1Fed
      default:
        return IntegrationType.Datadog
    }
  }

  return observabilityToolType.toLowerCase() as IntegrationType
}

function getExternalIntegrationUrl(
  integration: TeamExternalIntegrationResponse
) {
  const { type, url } = integration

  return ENDPOINT_URLS[type] ?? url
}

export function formatTeamExternalIntegrationFromTeamExternalIntegrationResponse(
  externalIntegrationResponse: TeamExternalIntegrationResponse
): TeamExternalIntegration {
  const integrationType = externalIntegrationResponse.observabilityToolType
    ? getIntegrationTypeFromObservabilityToolTypeAndDomain(
        externalIntegrationResponse.observabilityToolType,
        externalIntegrationResponse.domain
      )
    : (externalIntegrationResponse.type?.toLowerCase() as IntegrationType)

  const isAWS = integrationType === IntegrationType.AWS

  /*
   * This is a special case for Datadog US1 domain. The API response for this integration does not contain the domain,
   * so in order to facilitate Observability Tool authentication and Health Check creation, we manually add in the
   * "US1" domain.
   */
  if (integrationType === IntegrationType.Datadog) {
    externalIntegrationResponse.domain = 'US1'
  }

  const integrationTypeHeaders = externalIntegrationResponse.headers
    ? teamExternalIntegrationsObject[integrationType]?.headers
    : undefined

  const integrationTypeHeadersLookup = integrationTypeHeaders
    ? createLookupMapFromArray<StatusCheckHeader>(integrationTypeHeaders, 'key')
    : undefined

  return {
    identifier: externalIntegrationResponse.identifier,
    name: externalIntegrationResponse.name,
    domain: externalIntegrationResponse.domain,
    observabilityToolType: externalIntegrationResponse.observabilityToolType,
    url: getExternalIntegrationUrl(externalIntegrationResponse),
    createdAt: externalIntegrationResponse.createdAt,
    isConfigured: true,
    type: integrationType ?? IntegrationType.Custom,
    isPrivateNetworkIntegration: externalIntegrationResponse.privateNetwork,
    lastAuthenticationStatus:
      externalIntegrationResponse.lastAuthenticationStatus,
    canBeUsedAsStatusCheck: externalIntegrationResponse.canBeUsedAsStatusCheck,
    sortIndex:
      (integrationType &&
        teamExternalIntegrationsObject[integrationType]?.sortIndex) ??
      6,
    ...(isAWS && {
      integrationSpecificValues: getAWSIntegrationSpecificValues(
        externalIntegrationResponse
      )
    }),
    ...(externalIntegrationResponse.headers && {
      headers: Object.entries(externalIntegrationResponse.headers)
        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
        .map(([key, value]) => {
          return {
            key,
            value,
            label: integrationTypeHeadersLookup?.[key]?.label
          }
        })
    }),
    ...(externalIntegrationResponse.headersArn && {
      headersArn: externalIntegrationResponse.headersArn
    })
  }
}

export const convertResponseToTeamExternalIntegration = (
  integrations: TeamExternalIntegrationResponse[] | undefined = []
): TeamExternalIntegration[] => {
  return (
    integrations.map(
      formatTeamExternalIntegrationFromTeamExternalIntegrationResponse
    ) ?? []
  ).sort((a, b) => a.sortIndex - b.sortIndex)
}

export const removeSavedTeamExternalIntegrations = (
  integrations: TeamExternalIntegration[],
  savedIntegrations: TeamExternalIntegration[]
): TeamExternalIntegration[] => {
  const savedIntegrationsNames = savedIntegrations.map((i) => i.name)
  return integrations.filter((i) => !savedIntegrationsNames.includes(i.name))
}

export const completeTeamExternalIntegrations = (
  externalIntegrationType: ExternalIntegrationType,
  integrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  switch (externalIntegrationType) {
    case ExternalIntegrationType.StatusCheck:
      return completeStatusCheckIntegrations(integrations, includeCustom)
    case ExternalIntegrationType.LoadGenerator:
      return completeLoadGeneratorIntegrations(integrations, includeCustom)
  }
}

export const getUnsavedExternalIntegrations = (
  externalIntegrationType: ExternalIntegrationType,
  savedTeamExternalIntegrations: TeamExternalIntegration[]
) => {
  return removeSavedTeamExternalIntegrations(
    completeTeamExternalIntegrations(externalIntegrationType, [], true),
    savedTeamExternalIntegrations
  )
}

export const getAllExternalIntegrations = (
  externalIntegrationType: ExternalIntegrationType,
  savedTeamExternalIntegrations: TeamExternalIntegration[]
) => {
  return completeTeamExternalIntegrations(
    externalIntegrationType,
    savedTeamExternalIntegrations,
    true
  )
}

export const completeStatusCheckIntegrations = (
  existingIntegrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  const existingIntegrationTypes = new Set(
    existingIntegrations?.map(({ type }) => type) ?? []
  )
  const nonConfiguredIntegrations = SUPPORTED_EXTERNAL_INTEGRATIONS.filter(
    ({ type }) => !existingIntegrationTypes.has(type)
  )

  return [
    ...existingIntegrations,
    ...nonConfiguredIntegrations,
    ...(includeCustom ? [customStatusCheckIntegration] : []),
    // always push a local instance of the awsStatusCheckIntegration object since all AWS integrations will have the same `aws` type
    awsStatusCheckIntegration
  ].sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
}

export const completeLoadGeneratorIntegrations = (
  integrations: TeamExternalIntegration[],
  includeCustom?: boolean
): TeamExternalIntegration[] => {
  const completedIntegrations = [...integrations]
  if (
    !integrations.find((i) => i?.type?.toLowerCase() === IntegrationType.K6)
  ) {
    completedIntegrations.push(k6LoadGeneratorIntegration)
  }
  if (includeCustom) {
    completedIntegrations.push(customLoadGeneratorIntegration)
  }
  return completedIntegrations.sort(
    (a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0)
  )
}

export function computeResponseBodyEvaluation(
  integration: TeamExternalIntegration
) {
  switch (integration.type ?? IntegrationType.Custom) {
    case IntegrationType.Datadog:
      return datadogResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogUS3:
      return datadogUS3ResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogUS5:
      return datadogUS5ResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogEU:
      return datadogEUResponseBodyEvaluationConfiguration
    case IntegrationType.DatadogUS1Fed:
      return datadogUS1FedResponseBodyEvaluationConfiguration
    case IntegrationType.Pagerduty:
      return pagerDutyResponseBodyEvaluationConfiguration
    case IntegrationType.Prometheus:
      return prometheusResponseBodyEvaluationConfiguration
    case IntegrationType.Newrelic:
      return computeNewRelicResponseBodyEvaluationConfiguration(
        integration.url ?? ''
      )
    case IntegrationType.Grafana:
      return grafanaResponseBodyEvaluationConfiguration
    case IntegrationType.Dynatrace:
      return dynatraceResponseBodyEvaluationConfiguration
    case IntegrationType.Appdynamics:
      return appdynamicsResponseBodyEvaluationConfiguration
    case IntegrationType.AWS:
      return awsResponseBodyEvaluationConfiguration
  }
}

export async function getEndpointConfigurationUrl(
  integration: TeamExternalIntegration,
  teamId: string
): Promise<string> {
  if (!integration.url) {
    return ''
  }

  const trimmedUrl = integration.url?.trim()

  switch (integration.type) {
    case IntegrationType.Appdynamics:
      return integration.isPrivateNetworkIntegration
        ? trimmedUrl
        : convertToAppDynamicsUrl(integration.url)
    case IntegrationType.AWS:
      return convertToAWSApiUrl(integration.url)
    case IntegrationType.Datadog:
      return convertToDatadogApiUrl(integration.url)
    case IntegrationType.DatadogEU:
      return convertToDatadogEUApiUrl(integration.url)
    case IntegrationType.DatadogUS1Fed:
      return convertToDatadogUS1FedApiUrl(integration.url)
    case IntegrationType.DatadogUS3:
      return convertToDatadogUS3ApiUrl(integration.url)
    case IntegrationType.DatadogUS5:
      return convertToDatadogUS5ApiUrl(integration.url)
    case IntegrationType.Dynatrace:
      return integration.isPrivateNetworkIntegration
        ? trimmedUrl
        : convertToDynatraceUrl(integration.url) ?? ''
    case IntegrationType.Grafana:
      return integration.isPrivateNetworkIntegration
        ? trimmedUrl
        : convertToGrafanaUrl(integration.url)
    case IntegrationType.K6:
      return convertToK6Url(integration.url)?.trim() ?? ''
    case IntegrationType.Newrelic:
      return convertToNewRelicUrl(integration.url)?.trim() ?? ''
    case IntegrationType.Pagerduty:
      return await convertToPagerDutyUrl(integration, teamId)

    default:
      return trimmedUrl
  }
}

export function convertToBaseUrl(
  type: IntegrationType,
  url: string | undefined
) {
  if (!url) return ''

  switch (type) {
    case IntegrationType.Grafana:
      return convertToGrafanaBaseUrl(url)
    case IntegrationType.Dynatrace:
      return convertToDynatraceBaseUrl(url)
    case IntegrationType.Appdynamics:
      return convertToAppDynamicsBaseUrl(url)
    default:
      return url.trim()
  }
}

const isPredicateEmpty = (predicate) => {
  return !predicate.jpQuery?.trim() && !predicate.rValue?.trim()
}

export const formatResponseBodyEvaluationPredicates = ({
  op,
  predicates
}: ResponseBodyEvaluation) => {
  const hasNoPredicates =
    predicates?.length === 1 && isPredicateEmpty(predicates[0])

  return {
    op,
    predicates: hasNoPredicates
      ? []
      : predicates?.map((p) => ({
          ...p,
          jpQuery: p.jpQuery?.startsWith('.') ? p.jpQuery : `.${p.jpQuery}`
        })) ?? []
  }
}

export const addAuthorizationPrefixIfNeeded = (
  type: IntegrationType,
  authHeader: StatusCheckHeader
) => {
  const OBFUSCATION_PREFIX = '****'
  if (
    authHeader.key === 'Authorization' &&
    !authHeader.value.trim().startsWith(OBFUSCATION_PREFIX)
  ) {
    switch (type) {
      case IntegrationType.Pagerduty:
        return authHeader.value.trim().startsWith('Token token=')
          ? authHeader.value
          : `Token token=${authHeader.value.trim()}`
      case IntegrationType.Grafana:
      case IntegrationType.Appdynamics:
        return authHeader.value.trim().startsWith('Bearer ')
          ? authHeader.value
          : `Bearer ${authHeader.value.trim()}`
      case IntegrationType.Dynatrace:
        return authHeader.value.trim().startsWith('Api-Token ')
          ? authHeader.value
          : `Api-Token ${authHeader.value.trim()}`
      case IntegrationType.K6:
        return authHeader.value.trim().startsWith('token ')
          ? authHeader.value
          : `token ${authHeader.value.trim()}`
      default:
        return authHeader.value
    }
  }
  return authHeader.value
}

export function prepareHeaders(integration: TeamExternalIntegration) {
  const initialHeaders: Record<string, string> =
    integration.type === IntegrationType.Dynatrace
      ? { 'Content-Type': 'application/json' }
      : {}

  const headers = integration.headers?.reduce((result, item) => {
    result[item.key] = addAuthorizationPrefixIfNeeded(integration.type, item)
    return result
  }, initialHeaders)

  return headers
}

export async function getResponseBodyEvaluationByIntegrationType(
  integration: TeamExternalIntegration,
  evaluationConfiguration: StatusCheckFormEvaluationConfiguration,
  teamId: string
) {
  if (
    evaluationConfiguration.responseBodyEvaluation &&
    (integration.type === IntegrationType.Custom ||
      integration.observabilityToolType === ObservabilityToolType.CUSTOM)
  ) {
    return formatResponseBodyEvaluationPredicates(
      evaluationConfiguration.responseBodyEvaluation
    )
  } else if (
    integration.type === IntegrationType.Grafana ||
    integration.observabilityToolType === ObservabilityToolType.GRAFANA
  ) {
    return formatResponseBodyEvaluationPredicatesGrafana(
      await getEndpointConfigurationUrl(integration, teamId)
    )
  } else {
    computeResponseBodyEvaluation(integration)
  }
}

export function buildTeamExternalEndpointConfigurationRequest(
  integration: TeamExternalIntegration,
  endpointConfigurationUrl: string,
  statusCheckId?: string,
  authenticate?: boolean,
  endpointConfiguration?: EndpointConfiguration
) {
  const isAwsIntegration = isExternalIntegrationOfType(
    IntegrationType.AWS,
    integration
  )
  const isCustomIntegration = isExternalIntegrationOfType(
    IntegrationType.Custom,
    integration
  )
  const isPostMethod = endpointConfiguration?.method === 'POST'

  const endpointConfigurationHeadersFromIntegration =
    authenticate ||
    isCustomIntegration ||
    (!statusCheckId && !integration.isConfigured)
      ? prepareHeaders(integration)
      : undefined

  return {
    endpointConfiguration: {
      /*
       * Including additional endpoint configuration details is only valid
       * when we have configured the method to be 'POST'
       */
      ...(isPostMethod && endpointConfiguration),
      ...(integration.headersArn
        ? { headersArn: integration.headersArn }
        : {
            /*
             * We have to consider mixing in headers from the Authentication details,
             * onto those that were defined via the create/edit Status Check form.
             */
            headers: {
              ...endpointConfigurationHeadersFromIntegration,
              // This is only valid if we have configured the method to be 'POST'
              ...(isPostMethod &&
                endpointConfiguration?.headers?.['content-type'] && {
                  ['content-type']:
                    endpointConfiguration.headers['content-type']
                })
            }
          }),
      url: encodeQueryParams(endpointConfigurationUrl)
    },
    /*
     * only need to send `integrationSpecificValues` when we are
     * in the authenticate flow. i.e. we don't need to send this
     * in the create/edit health check flow
     */
    ...(isAwsIntegration &&
      authenticate && {
        integrationSpecificValues: getAWSIntegrationSpecificValues(integration)
      }),
    ...(integration.isConfigured
      ? {
          teamExternalIntegration: {
            observabilityToolType: integration.observabilityToolType,
            domain: integration.domain,
            name: integration.name
          }
        }
      : statusCheckId && { webhookId: statusCheckId })
  }
}

export const isEncoded = (uri) => uri !== decodeURIComponent(uri || '')

export const encodeQueryParams = (url: string) => {
  if (!isEncoded(url)) {
    let Url: URL
    try {
      Url = new URL(url)
    } catch (e) {
      // If we get an invalid URL just give up
      return url
    }

    const fragment = Url.hash
    const [requestUrl, queryParamsString] = url
      .replace(fragment, '')
      .split(/\?(.*)/s)

    if (queryParamsString) {
      const encodedParams: string[] = []
      queryParamsString.split('&').forEach((queryParam) => {
        const [param, value] = queryParam.split('=')
        const encodedParam = value
          ? `${param}=${encodeURIComponent(value)}`
          : param

        if (!encodedParam) return

        encodedParams.push(encodedParam)
      })
      return fragment
        ? `${requestUrl}?${encodedParams.join('&')}${fragment}`
        : `${requestUrl}?${encodedParams.join('&')}`
    }
  }

  return url
}

export const isValidStatusCodeOrRange = (v) => /^2\d{2}(-2\d{2})?$/.test(v) // 2xx or 2xx-2xx
